import apiClient from "./client";

const API_BASE = process.env.REACT_APP_API_URL || "/api/v1";

export async function getForumCategories() {
  return apiClient.get("/forum/categories");
}

export async function getForumBoards() {
  return apiClient.get("/forum/boards");
}

export async function getForumBoardCategories(boardId) {
  return apiClient.get(`/forum/boards/${boardId}/categories`);
}

export async function getForumEmojis({ all = false } = {}) {
  const params = all ? "?all=true" : "";
  return apiClient.get(`/forum/emojis${params}`);
}

export async function getForumCategoryThreads(categoryId, { page = 1, limit = 20 } = {}) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  return apiClient.get(`/forum/categories/${categoryId}/threads?${params}`);
}

export async function createForumThread(categoryId, data) {
  return apiClient.post(`/forum/categories/${categoryId}/threads`, data);
}

export async function getForumThread(threadId, { page = 1, limit = 25 } = {}) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  return apiClient.get(`/forum/threads/${threadId}?${params}`);
}

export async function getForumPostLocation(postId, { limit = 25 } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  return apiClient.get(`/forum/posts/${postId}/location?${params}`);
}

/** @param {{ body: string, quotedPostId?: number }} | string bodyOrPayload */
export async function createForumPost(threadId, bodyOrPayload) {
  const payload =
    typeof bodyOrPayload === "string" ? { body: bodyOrPayload } : bodyOrPayload;
  const body = { body: payload.body };
  if (payload.quotedPostId != null) {
    body.quotedPostId = payload.quotedPostId;
  }
  return apiClient.post(`/forum/threads/${threadId}/posts`, body);
}

export async function getForumNotificationsUnreadCount() {
  return apiClient.get("/forum/me/notifications/unread-count");
}

export async function getForumNotifications(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== "") qs.set(k, String(v));
  });
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiClient.get(`/forum/me/notifications${suffix}`);
}

export async function markForumNotificationRead(notificationId) {
  return apiClient.post(`/forum/me/notifications/${notificationId}/read`, {});
}

export async function markAllForumNotificationsRead() {
  return apiClient.post("/forum/me/notifications/read-all", {});
}

export async function updateForumPost(postId, body) {
  return apiClient.put(`/forum/posts/${postId}`, { body });
}

export async function toggleForumPostThumbsUp(postId) {
  return apiClient.post(`/forum/posts/${postId}/thumbs-up`);
}

export async function deleteForumPost(postId) {
  return apiClient.delete(`/forum/posts/${postId}`);
}

export async function restoreForumPost(postId) {
  return apiClient.post(`/forum/posts/${postId}/restore`, {});
}

export async function patchForumThread(threadId, patch) {
  return apiClient.patch(`/forum/threads/${threadId}`, patch);
}

export async function deleteForumThread(threadId) {
  return apiClient.delete(`/forum/threads/${threadId}`);
}

export async function restoreForumThread(threadId) {
  return apiClient.post(`/forum/threads/${threadId}/restore`, {});
}

export async function getForumProfile() {
  return apiClient.get("/forum/me/profile");
}

/** Unwrap GET /forum/me/profile for route cache (prefetch stores profile only). */
export function normalizeForumProfileData(data) {
  if (!data || typeof data !== "object") return {};
  if ("profile" in data) {
    const profile = data.profile;
    return profile != null && typeof profile === "object" ? profile : {};
  }
  return data;
}

export async function patchForumProfile(patch) {
  return apiClient.patch("/forum/me/profile", patch);
}

/** Staff */
export async function createForumCategory(data) {
  return apiClient.post("/forum/categories", data);
}

export async function patchForumCategory(categoryId, patch) {
  return apiClient.patch(`/forum/categories/${categoryId}`, patch);
}

export async function deleteForumCategory(categoryId) {
  return apiClient.delete(`/forum/categories/${categoryId}`);
}

export async function uploadForumImage(file) {
  const formData = new FormData();
  formData.append("image", file);
  const headers = {};
  if (apiClient.accessToken) {
    headers.Authorization = `Bearer ${apiClient.accessToken}`;
  }
  const response = await fetch(`${API_BASE}/forum/upload-image`, {
    method: "POST",
    headers,
    body: formData,
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Image upload failed");
  }
  return data;
}

export async function uploadForumSignatureImage(file) {
  const formData = new FormData();
  formData.append("image", file);
  const headers = {};
  if (apiClient.accessToken) {
    headers.Authorization = `Bearer ${apiClient.accessToken}`;
  }
  const response = await fetch(`${API_BASE}/forum/upload-signature-image`, {
    method: "POST",
    headers,
    body: formData,
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Signature image upload failed");
  }
  return data;
}

export async function uploadForumProfileIcon(file) {
  const formData = new FormData();
  formData.append("image", file);
  const headers = {};
  if (apiClient.accessToken) {
    headers.Authorization = `Bearer ${apiClient.accessToken}`;
  }
  const response = await fetch(`${API_BASE}/forum/upload-profile-icon`, {
    method: "POST",
    headers,
    body: formData,
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Profile icon upload failed");
  }
  return data;
}

export async function createForumEmoji({ shortcode, displayName, file }) {
  const formData = new FormData();
  formData.append("shortcode", shortcode);
  formData.append("displayName", displayName);
  formData.append("emoji", file);
  const headers = {};
  if (apiClient.accessToken) {
    headers.Authorization = `Bearer ${apiClient.accessToken}`;
  }
  const response = await fetch(`${API_BASE}/forum/emojis`, {
    method: "POST",
    headers,
    body: formData,
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Emoji upload failed");
  }
  return data;
}

export async function patchForumEmoji(emojiId, patch) {
  return apiClient.patch(`/forum/emojis/${emojiId}`, patch);
}

export async function deleteForumEmoji(emojiId) {
  return apiClient.delete(`/forum/emojis/${emojiId}`);
}
